import { createAsyncThunk } from '@reduxjs/toolkit';
import every from 'lodash/every';
import map from 'lodash/map';
import zipObject from 'lodash/zipObject';
import factoryOptionsApi from '../../../api/factoryOptionsApi';
import tobaccoSalesApi from '../../../api/tobaccoSalesApi';

// ---------------------------------------------------------------------

const REDUCER_NAME = 'tobaccoSales';

export const GetTobaccoSales = createAsyncThunk<any, any>(
  `${REDUCER_NAME}/getTobaccoSales/inventory`,
  async (params, thunkAPI) => {
    const res = await tobaccoSalesApi.getInventory(params);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const UpdateTobaccoSale = createAsyncThunk<any, any>(
  `${REDUCER_NAME}/updateTobaccoSale`,
  async ({ id, data }, thunkAPI) => {
    const res = await tobaccoSalesApi.update(id, data);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const CancelTobaccoSale = createAsyncThunk<any, string>(
  `${REDUCER_NAME}/cancelTobaccoSale`,
  async (id, thunkAPI) => {
    const res = await tobaccoSalesApi.cancel(id);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const DeleteTobaccoSaleContainer = createAsyncThunk<any, any>(
  `${REDUCER_NAME}/deleteTobaccoSaleContainer`,
  async (saleContainerToDelete, thunkAPI) => {
    const res = await tobaccoSalesApi.delete(saleContainerToDelete);

    if (res?.status === 200) {
      return { saleContainerToDelete };
    }

    return thunkAPI.rejectWithValue(null);
  }
);

export const CreateFactoryOption = createAsyncThunk<any, any>(
  `${REDUCER_NAME}/createFactoryOption`,
  async (data, thunkAPI) => {
    const res = await factoryOptionsApi.create(data);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetTobaccoSaleOptions = createAsyncThunk(
  `${REDUCER_NAME}/getTobaccoSalesOptions`,
  async (__, thunkAPI) => {
    const optionNames = ['customers'];

    const optionResponses = await Promise.all(map(optionNames, (name) => factoryOptionsApi.getByName(name)));

    const allResponsesSuccessful = every(optionResponses, (res) => res.status === 200);

    if (allResponsesSuccessful) {
      const data = map(optionResponses, 'data');
      return zipObject(optionNames, data);
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const DownloadTobaccoSaleReportXlsx = createAsyncThunk<any, any>(
  'payroll/downloadTobaccoSaleXlsx',
  async ({ id, type, tsNumber, invoiceNumber, customerName }, thunkAPI) => {
    try {
      const res = await tobaccoSalesApi.getXlsx(id, type);

      if (res.status === 200) {
        const { data } = res;
        const blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);

        const currentDate = new Date();
        const formattedDate = currentDate.toISOString().split('T')[0];
        const fileNameParts = [`TOBACCO_SALE_REPORT#${tsNumber}`];
        fileNameParts.push(`_${type}`);

        if (invoiceNumber) {
          fileNameParts.push(`INVOICE_${invoiceNumber}`);
        }

        if (customerName) {
          fileNameParts.push(`CUSTOMER_${customerName}`);
        }

        fileNameParts.push(`${formattedDate}.xlsx`);

        const fileName = fileNameParts.join('_');

        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      return res;
    } catch {
      return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
    }
  }
);

export const DownloadFilteredTobaccoSalesReportXlsx = createAsyncThunk<any, any>(
  'tobaccoSales/downloadFilteredTobaccoSalesXlsx',
  async (params, thunkAPI) => {
    try {
      const res = await tobaccoSalesApi.getFilteredXlsx(params);

      if (res.status === 200) {
        const { data } = res;
        const blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);

        const formattedDate = new Date().toISOString().split('T')[0];
        const fileName = `TOBACCO_SALES_REPORT_${formattedDate}.xlsx`;

        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      return res;
    } catch {
      return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
    }
  }
);
